import { FC, MouseEvent, useLayoutEffect, useState } from 'react';
import { Hue, Saturation } from 'react-color/lib/components/common';
import { useI18nContext } from '@i18n/i18n-react';
import { Button } from '@components/button/Button';
import { TextInput } from '@components/form';
import { FlexRow, FlexGrow } from '@components/layout/Flex';
import { useDebounce } from '@hooks/useDebounce';

import { ColorPickBadge, ColorPickCanvas, ColorPickCanvasCircle, ColorPickSliderKnob } from './Color.style';

// @ts-expect-error no types
import tinycolor from 'tinycolor2';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ColorResult = any;

export type ColorPickCustomProps = {
    value: string | null;
    disabled?: boolean;
    onChange?: (color: string) => void;
    onCommit?: () => void;
    onCancel?: () => void;
};

const preventDefault = (e: MouseEvent) => e.preventDefault();

export const ColorPickCustom: FC<ColorPickCustomProps> = (props: ColorPickCustomProps) => {
    const { value, onChange, onCommit } = props;

    const { LL } = useI18nContext();

    const [customColor, setCustomColor] = useState(() => toColorState(value));
    const [hexColor, setHexColor] = useState(() => customColor.hex);

    const updateCustomColor = (color: string | null) => {
        // Preserve hue when going through gray like in 'react-color'
        const state = toColorState(color, customColor.oldHue);
        setCustomColor(state);
        setHexColor(state.hex);
    };

    const updateHexColor = (color: string) => {
        const c = color.trim();
        if (c.match(/^#([0-9a-fA-F]{6})$/)) updateCustomColor(c);

        setHexColor(color);
    };

    const [lastValue, setLastValue] = useState(value);
    if (lastValue !== value) {
        updateCustomColor(value);
        setLastValue(value);
    }

    // Debounce to outside
    const debouncedColor = useDebounce(customColor.hex, 300);
    useLayoutEffect(() => {
        if (value !== debouncedColor) {
            onChange?.(debouncedColor);
        }
        // Only if value changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedColor]);

    // Avoid DOM error
    const { oldHue, ...colorProps } = customColor;

    return (
        <>
            <ColorPickCanvas $width={216} $height={159} onMouseDown={preventDefault}>
                <Saturation
                    {...colorProps}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    pointer={ColorPickCanvasCircle as any}
                    onChange={(value) => setCustomColor(toColorState(value, oldHue))}
                />
            </ColorPickCanvas>
            <ColorPickCanvas $width={216} $height={16} onMouseDown={preventDefault}>
                <Hue
                    {...colorProps}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    pointer={ColorPickSliderKnob as any}
                    onChange={(value) => setCustomColor(toColorState(value))}
                />
            </ColorPickCanvas>
            <FlexRow $gap={8}>
                <ColorPickBadge
                    $width={24}
                    $height={24}
                    style={{ backgroundColor: customColor.hex }}
                    onClick={onCommit}
                />
                <TextInput size="small" width={80} value={hexColor} onChange={updateHexColor} />
                <FlexGrow />

                <Button title={LL.DONE()} type="button" $size="small" $type="tertiary" onClick={onCommit} />
            </FlexRow>
        </>
    );
};

export const toColorState = (data?: string | ColorResult | null, oldHue?: number) => {
    const color = tinycolor(data.hex ?? data ?? '#808080');

    const hsl = color.toHsl();
    const hsv = color.toHsv();
    const rgb = color.toRgb();
    const hex = color.toHex();

    hsl.h = oldHue ?? data.h ?? hsl.h;
    hsv.h = oldHue ?? data.h ?? hsv.h;

    const transparent = hex === '000000' && rgb.a === 0;

    return {
        hsl: hsl,
        hex: transparent ? 'transparent' : '#' + hex,
        rgb: rgb,
        hsv: hsv,
        oldHue: oldHue ?? data.h ?? hsl.h,
        source: data.source,
    };
};
